// =============================================================================
// Document Viewer Modal
// Displays documents (PDFs, images) inline in a modal overlay
// Uses secure temporary links for iframe embedding
// Based on Sarsen CRM FileViewer implementation
// =============================================================================

import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface DocumentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  fileName: string;
  mimeType?: string;
  getEmbedUrls: (documentId: string) => Promise<{
    embedUrl: string;
    downloadUrl: string;
    previewUrl: string;
    proxyUrl: string;
    fileName: string;
    fileType: string;
    mimeType: string;
  }>;
}

export function DocumentViewerModal({
  isOpen,
  onClose,
  documentId,
  fileName,
  mimeType: initialMimeType = '',
  getEmbedUrls,
}: DocumentViewerModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [_previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [proxyUrl, setProxyUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>(initialMimeType);

  useEffect(() => {
    if (isOpen && documentId) {
      setLoading(true);
      setError(null);
      setEmbedUrl(null);
      setDownloadUrl(null);
      setPreviewUrl(null);
      setProxyUrl(null);

      getEmbedUrls(documentId)
        .then((result) => {
          setEmbedUrl(result.embedUrl);
          setDownloadUrl(result.downloadUrl);
          setPreviewUrl(result.previewUrl);
          setProxyUrl(result.proxyUrl);
          setMimeType(result.mimeType || initialMimeType);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load document:', err);
          setError(err.message || 'Failed to load document');
          setLoading(false);
        });
    }
  }, [isOpen, documentId, getEmbedUrls, initialMimeType]);

  // File type detection - based on mimeType
  const isImage = (type: string): boolean => type.startsWith('image/');
  const isPdf = (type: string): boolean => type === 'application/pdf';
  const isSpreadsheet = (type: string): boolean => type.includes('spreadsheet') || type.includes('excel');
  const isWordDoc = (type: string): boolean => type.includes('word') || type.includes('msword') || type.includes('document');
  const isPowerPoint = (type: string): boolean => type.includes('powerpoint') || type.includes('presentation');
  const isOfficeDoc = (type: string): boolean => isSpreadsheet(type) || isWordDoc(type) || isPowerPoint(type);

  // Microsoft Office Online viewer URL for Office documents
  const getOfficeViewerUrl = (url: string): string => {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  };

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading document...</p>
          </div>
        </div>
      );
    }

    if (error || !embedUrl) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <XMarkIcon className="h-8 w-8 text-red-600" />
            </div>
            <p className="text-lg font-medium text-gray-900 mb-2">Unable to load document</p>
            <p className="text-gray-600 mb-4">{error || 'Failed to get document URL'}</p>
            {downloadUrl && (
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download File
              </button>
            )}
          </div>
        </div>
      );
    }

    // Images - display directly using proxy URL to avoid CORS/X-Frame-Options issues
    if (isImage(mimeType)) {
      return (
        <div className="flex items-center justify-center h-full p-4 overflow-auto">
          <img
            src={proxyUrl || embedUrl}
            alt={fileName}
            className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
            onError={() => setError('Failed to load image')}
          />
        </div>
      );
    }

    // PDFs - use iframe with proxy URL for direct inline viewing
    if (isPdf(mimeType)) {
      return (
        <div className="h-full w-full">
          <iframe
            src={proxyUrl || embedUrl}
            title={fileName}
            className="w-full h-full border-0"
            onError={() => setError('Failed to load PDF')}
          />
        </div>
      );
    }

    // Office documents - use Microsoft Office Online Viewer
    if (isOfficeDoc(mimeType) && downloadUrl) {
      const officeViewerUrl = getOfficeViewerUrl(downloadUrl);
      return (
        <div className="h-full w-full">
          <iframe
            src={officeViewerUrl}
            title={fileName}
            className="w-full h-full border-0"
          />
        </div>
      );
    }

    // For other file types, show download option
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
            <svg className="h-8 w-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-lg font-medium text-gray-900 mb-2">{fileName}</p>
          <p className="text-gray-600 mb-6">Preview not available for this file type</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 px-6 py-3 text-base font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Download
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl h-[90vh] transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900 truncate max-w-[60%]">
                    {fileName}
                  </Dialog.Title>
                  <div className="flex items-center gap-2">
                    {downloadUrl && (
                      <button
                        onClick={handleDownload}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Download</span>
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Close"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden bg-gray-100">
                  {renderContent()}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default DocumentViewerModal;
